// useVisualConstants.ts
import { GithubFormat } from './formatCode';
import { Constant } from '../components/GameSettingsPanel';
import { formatConstantNameToLabel, formatLabelToConstantName } from './labelHelpers';

const visualFileCandidates = ['VisualSettings.ts', 'VisualConstants.ts'];

export const extractVisualConstants = (code: GithubFormat): Constant[] => {
    // console.log('Starting extractVisualConstants');
    // console.log('Code received for extraction:', code);
    const constantsFile = code.find(file => {
        return visualFileCandidates.some(name => file.path.toLowerCase().includes(name.toLowerCase()));
    });

    if (!constantsFile) {
        // console.warn('VisualConstants.ts not found in code during extraction');
        return [];
    }

    // console.log('VisualConstants.ts found:', constantsFile.path);
    // console.log('Content of VisualConstants.ts:', constantsFile.content);

    let constants: Constant[] = [];
    const seenLabels = new Set<string>();

    // --- Attempt to extract constants from direct export format (colors and numbers) ---
    // console.log('Attempting to extract direct constants (colors)');
    // Matches hex (#RRGGBB, #RGB), 0xRRGGBB, or rgb/rgba(...) strings (quoted with ', " or `)
    const directColorQuotedRegex = /export const (\w+)\s*=\s*([\'"`](#[0-9a-fA-F]{3,6}|0x[0-9a-fA-F]{6}|rgba\([^)]*\)|rgb\([^)]*\))[\'"`]);/gi;
    let match;
    while ((match = directColorQuotedRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(match[1]);
        if (seenLabels.has(label)) continue;
        // console.log('Direct constant label:', label);
        const value = convertColorToHex(match[2].replace(/[\'\"`]/g, ''));
        // console.log('Direct constant value (converted):', value);
        constants.push({ label, value, type: 'color' });
        seenLabels.add(label);
    }
    // console.log('Direct constants extracted:', constants);

    // Colors (unquoted 0xRRGGBB)
    const directColor0xRegex = /export const (\w+)\s*=\s*(0x[0-9a-fA-F]{6});/g;
    let match0x;
    while ((match0x = directColor0xRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(match0x[1]);
        if (seenLabels.has(label)) continue;
        const value = convertColorToHex(match0x[2]);
        constants.push({ label, value, type: 'color' });
        seenLabels.add(label);
    }

    // Numbers (direct export)
    const directNumberRegex = /export const (\w+)\s*=\s*(-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?);/g;
    let matchNum;
    while ((matchNum = directNumberRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(matchNum[1]);
        if (seenLabels.has(label)) continue;
        constants.push({ label, value: matchNum[2].trim(), type: 'number' });
        seenLabels.add(label);
    }

    // Booleans (direct export)
    const directBooleanRegex = /export const (\w+)\s*=\s*(true|false);/gi;
    let matchBool;
    while ((matchBool = directBooleanRegex.exec(constantsFile.content)) !== null) {
        const label = formatConstantNameToLabel(matchBool[1]);
        if (seenLabels.has(label)) continue;
        constants.push({ label, value: matchBool[2].toLowerCase(), type: 'boolean' });
        seenLabels.add(label);
    }


    // --- Extract constants explicitly from the VISUAL_SETTINGS or VISUAL_CONSTANTS object ---
    // This regex targets key-value pairs within either object where the value is a color string
    const visualObjectRegex = /export const (VISUAL_SETTINGS|VISUAL_CONSTANTS)\s*=\s*({[\s\S]*?})/;
    const visualConstantsMatch = visualObjectRegex.exec(constantsFile.content);
    
    if (visualConstantsMatch) {
        const constantsContent = visualConstantsMatch[2];
        // General pair capture (flat only)
        const pairRegex = /(\w+):\s*([^,\n}]+),?/g;
        let pairMatch;
        while ((pairMatch = pairRegex.exec(constantsContent)) !== null) {
            const constName = pairMatch[1];
            const rawToken = pairMatch[2].trim();
            const label = formatConstantNameToLabel(constName);
            if (seenLabels.has(label)) continue;

            const tokenNoComments = stripInlineCommentsOutsideQuotes(rawToken);
            const unquoted = stripQuotes(tokenNoComments);
            if (isColorToken(unquoted)) {
                const value = convertColorToHex(unquoted);
                constants.push({ label, value, type: 'color' });
                seenLabels.add(label);
                continue;
            }
            if (isNumericLiteral(unquoted)) {
                constants.push({ label, value: unquoted, type: 'number' });
                seenLabels.add(label);
                continue;
            }
            if (isBooleanLiteral(unquoted)) {
                constants.push({ label, value: unquoted.toLowerCase(), type: 'boolean' });
                seenLabels.add(label);
                continue;
            }
            // Ignore non-color, non-numeric, non-boolean tokens (e.g., strings/fonts) for now
        }
    }
    // console.log('Object constants extracted:', constants);

    // --- Extract arrays of colors, numbers, or booleans (direct export) ---
    // Only accept arrays where ALL elements are same-type
    const directArrayRegex = /export const (\w+)\s*=\s*\[([\s\S]*?)\];/g;
    let directArrayMatch;
    while ((directArrayMatch = directArrayRegex.exec(constantsFile.content)) !== null) {
        const arrayName = directArrayMatch[1];
        const arrayBody = directArrayMatch[2];
        const tokens = tokenizeArrayItems(arrayBody);
        if (tokens.length === 0) continue;
        const withoutComments = tokens.map(t => stripInlineCommentsOutsideQuotes(t.trim()));
        const unquoted = withoutComments.map(t => stripQuotes(t.trim()));
        const allColors = unquoted.every(v => isColorToken(v));
        const allNumbers = unquoted.every(v => isNumericLiteral(v));
        const allBooleans = unquoted.every(v => isBooleanLiteral(v));
        if (!allColors && !allNumbers && !allBooleans) continue; // skip mixed/invalid arrays
        const labelBase = formatConstantNameToLabel(arrayName);
        for (let i = 0; i < unquoted.length; i++) {
            const label = `${labelBase} ${i + 1}`;
            if (allColors) {
                const value = convertColorToHex(unquoted[i]);
                constants.push({ label, value, type: 'color', groupKey: arrayName, groupIndex: i });
            } else if (allNumbers) {
                constants.push({ label, value: unquoted[i], type: 'number', groupKey: arrayName, groupIndex: i });
            } else {
                constants.push({ label, value: unquoted[i].toLowerCase(), type: 'boolean', groupKey: arrayName, groupIndex: i });
            }
        }
    }

    // --- Extract arrays of colors, numbers, or booleans inside VISUAL_SETTINGS / VISUAL_CONSTANTS ---
    if (visualConstantsMatch) {
        const constantsContent = visualConstantsMatch[2];
        const keyArrayPairRegex = /(\w+):\s*\[([\s\S]*?)\]/g;
        let arrPairMatch;
        while ((arrPairMatch = keyArrayPairRegex.exec(constantsContent)) !== null) {
            const keyName = arrPairMatch[1];
            const arrayBody = arrPairMatch[2];
            const tokens = tokenizeArrayItems(arrayBody);
            if (tokens.length === 0) continue;
            const withoutComments = tokens.map(t => stripInlineCommentsOutsideQuotes(t.trim()));
            const unquoted = withoutComments.map(t => stripQuotes(t.trim()));
            const allColors = unquoted.every(v => isColorToken(v));
            const allNumbers = unquoted.every(v => isNumericLiteral(v));
            const allBooleans = unquoted.every(v => isBooleanLiteral(v));
            if (!allColors && !allNumbers && !allBooleans) continue;
            const labelBase = formatConstantNameToLabel(keyName);
            for (let i = 0; i < unquoted.length; i++) {
                const label = `${labelBase} ${i + 1}`;
                if (allColors) {
                    const value = convertColorToHex(unquoted[i]);
                    constants.push({ label, value, type: 'color', groupKey: keyName, groupIndex: i });
                } else if (allNumbers) {
                    constants.push({ label, value: unquoted[i], type: 'number', groupKey: keyName, groupIndex: i });
                } else {
                    constants.push({ label, value: unquoted[i].toLowerCase(), type: 'boolean', groupKey: keyName, groupIndex: i });
                }
            }
        }
    }
    // console.log('Finished extractVisualConstants. Total constants found:', constants.length);
    return constants;
};

// Convert Title Case Spaces back to UPPER_CASE_WITH_UNDERSCORES
const getLabelToConstantName = (label: string): string => {
    return formatLabelToConstantName(label);
};

// Apply the updated constants back to the code
export const applyVisualConstants = (code: GithubFormat, constants: Constant[]): GithubFormat => {
    // console.log('Starting applyVisualConstants');
    // console.log('Constants to apply:', constants);
    // Find the VisualSettings.ts (preferred) or VisualConstants.ts file
    let constantsFileIndex = code.findIndex(file => {
        return visualFileCandidates.some(name => file.path.includes(name));
    });
    
    // If none found, create VisualSettings.ts with VISUAL_SETTINGS and apply values
    if (constantsFileIndex === -1) {
        // Group by arrays (groupKey present) and scalar constants (no groupKey)
        const grouped = new Map<string, Constant[]>();
        const scalars: Constant[] = [];
        for (const c of constants) {
            if (typeof c.groupKey === 'string' && typeof c.groupIndex === 'number') {
                if (!grouped.has(c.groupKey)) grouped.set(c.groupKey, []);
                grouped.get(c.groupKey)!.push(c);
            } else {
                scalars.push(c);
            }
        }

        // Build lines for scalars
        const scalarLines = scalars.map(c => {
            const name = formatLabelToConstantName(c.label);
            if (c.type === 'color') {
                return `${name}: '${c.value}'`;
            }
            if (c.type === 'number') {
                return `${name}: ${c.value}`;
            }
            if (c.type === 'boolean') {
                return `${name}: ${c.value.toLowerCase()}`;
            }
            return `${name}: '${c.value}'`;
        });

        // Build lines for grouped arrays
        const arrayLines: string[] = [];
        grouped.forEach((items, groupKey) => {
            const sorted = [...items].sort((a, b) => (a.groupIndex! - b.groupIndex!));
            const isColorArray = sorted.every(i => i.type === 'color');
            const renderedItems = sorted.map(i => {
                if (isColorArray) return `'${i.value.toLowerCase()}'`;
                return `${i.value}`;
            }).join(', ');
            arrayLines.push(`${groupKey}: [${renderedItems}]`);
        });

        const lines = [...scalarLines, ...arrayLines].join(',\n');
        const newContent = `// Do not rename or remove this file or comment.\n// Visual settings go here, so the user can easily tweak the values within the game settings panel.\n// Avoid using nested objects or arrays; all settings must be at the same level within the VISUAL_SETTINGS object. Prefer SNAKE_CASE for names.\nexport const VISUAL_SETTINGS = {\n${lines}\n};`;
        const newFilePath = 'src/config/VisualSettings.ts';
        const newFile = { path: newFilePath, content: newContent } as any;
        const updatedCodeOnCreate = [...code, newFile];
        return updatedCodeOnCreate as GithubFormat;
    }
    
    // console.log('VisualConstants.ts found at index:', constantsFileIndex);
    // Create a copy of the code array
    const updatedCode = [...code];
    const originalContent = updatedCode[constantsFileIndex].content;
    let updatedContent = originalContent;
    // console.log('Original content of VisualConstants.ts:', originalContent);
    
    // Extract all existing constants from the file to get their official names and original values
    const constantMap = new Map();
    // console.log('Populating constantMap from original content...');

    // --- Populate map from old direct export format ---
    // console.log('Attempting to map direct export constants');
    // const directConstantRegex = /export const (\w+)\s*=\s*([^;{]+?);/g;
    // let directMatch;
    // while ((directMatch = directConstantRegex.exec(originalContent)) !== null) {
    //     const constName = directMatch[1];
    //     const constValue = directMatch[2].trim(); // This is the original string value
    //     const label = formatConstantNameToLabel(constName);
    //     // console.log(`Mapped direct constant: Label='${label}', Name='${constName}', Value='${constValue}'`);
    //     constantMap.set(label, { name: constName, value: constValue, type: 'direct' });
    // }

    // --- Populate map from direct export format (legacy) ---
    const directConstantRegexMap = /export const (\w+)\s*=\s*([^;{]+?);/g;
    let directMatch;
    while ((directMatch = directConstantRegexMap.exec(originalContent)) !== null) {
        const constName = directMatch[1];
        const constValue = directMatch[2].trim();
        const label = formatConstantNameToLabel(constName);
        if (!constantMap.has(label)) {
            constantMap.set(label, { name: constName, value: constValue, type: 'direct' });
        }
    }

    // --- Populate map from VISUAL_SETTINGS then VISUAL_CONSTANTS (SETTINGS takes priority) ---
    const objectVariants = ['VISUAL_SETTINGS', 'VISUAL_CONSTANTS'];
    for (const parentName of objectVariants) {
        const exportedObjectConstantRegex = new RegExp(`export const ${parentName}\\s*=\\s*({[^}]*});?`, 'gs');
        let objectMatchResult;
        while ((objectMatchResult = exportedObjectConstantRegex.exec(originalContent)) !== null) {
            const constantsContent = objectMatchResult[1];
            const constantPairRegex = /(\w+):\s*([^,\n}]+),?/g;
            let pairMatch;
            while ((pairMatch = constantPairRegex.exec(constantsContent)) !== null) {
                const constName = pairMatch[1];
                const constValue = pairMatch[2].trim();
                const label = formatConstantNameToLabel(constName);
                if (!constantMap.has(label)) {
                    constantMap.set(label, { name: constName, value: constValue, type: 'object', parentObject: parentName });
                }
            }
        }
    }
    // console.log('Constant map populated:', constantMap);
    
    // Update each constant in the file content (scalar values only)
    constants.forEach(constant => {
        // console.log(`Processing constant to apply: ${JSON.stringify(constant)}`);
        if (constantMap.has(constant.label)) {
            const originalConst = constantMap.get(constant.label);
            // console.log('Original constant found in map:', originalConst);
            // Before comparison, normalize for consistent comparison
            const originalNormalizedValue = constant.type === 'color'
                ? convertColorToHex(stripQuotes(stripInlineCommentsOutsideQuotes(originalConst.value)))
                : stripQuotes(stripInlineCommentsOutsideQuotes(originalConst.value));
            
            if (originalNormalizedValue !== constant.value) { // Compare normalized values
                // console.log(`Value mismatch for ${constant.label}. Original (normalized): '${originalNormalizedValue}', New: '${constant.value}'`);
                let valueToApply = constant.value; // Start with the new hex value

                // If it's a color, convert it back to its original format
                if (constant.type === 'color') {
                    valueToApply = convertHexToOriginalFormat(constant.value, originalConst.value);
                    // console.log('Value to apply (converted to original format):', valueToApply);
                } else if (constant.type === 'number') {
                    // Always write numbers unquoted
                    valueToApply = `${constant.value}`;
                } else if (constant.type === 'boolean') {
                    // Always write booleans unquoted, lowercase
                    valueToApply = `${constant.value.toLowerCase()}`;
                } else if (typeof constant.value === 'string') {
                    // For non-color strings (like fonts), ensure they are quoted if original was quoted
                    if (originalConst.value.startsWith("'") || originalConst.value.startsWith('"')) {
                        valueToApply = `'${constant.value}'`;
                    }
                }

                if (originalConst.type === 'direct') {
                    // console.log('Updating direct constant.');
                    const constRegex = new RegExp(`(export const ${originalConst.name}\\s*=\\s*)([^;]+?)(\\s*(?:\\/\\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/))?(;)`, 'g');
                    updatedContent = updatedContent.replace(constRegex, `$1${valueToApply}$3$4`);
                    // console.log('Content after direct constant update:', updatedContent);
                } else if (originalConst.type === 'object') {
                    // console.log('Updating object constant.');
                    // Handle both middle and last properties using lookahead for comma or closing brace, preserving trailing inline comments
                    const parentObjectRegex = new RegExp(`(export const ${originalConst.parentObject}\\s*=\\s*{[^}]*?)(${originalConst.name}:\\s*)([^,\\n}]+?)(\\s*(?:\\/\\\/[^\\n]*|\\/\\*[\\s\\S]*?\\*\\/))?(?=,|\\s*})`, 's');
                    updatedContent = updatedContent.replace(parentObjectRegex, `$1$2${valueToApply}$4`);
                    // console.log('Content after object constant update:', updatedContent);
                }
            } else {
                // console.log(`Value for ${constant.label} is already up to date. No change needed.`);
            }
        } else {
            // console.warn(`Constant label '${constant.label}' not found in the original VisualConstants.ts file map.`);
        }
    });
    
    // --- Handle grouped array constants (colors, numbers, or booleans) ---
    const groupedByKey = new Map<string, Constant[]>();
    for (const c of constants) {
        if (typeof c.groupKey === 'string' && typeof c.groupIndex === 'number') {
            if (!groupedByKey.has(c.groupKey)) groupedByKey.set(c.groupKey, []);
            groupedByKey.get(c.groupKey)!.push(c);
        }
    }

    const replaceDirectArray = (content: string, arrayName: string, newItems: string[]): { content: string; replaced: boolean } => {
        const findRegex = new RegExp(`export const ${escapeRegExp(arrayName)}\\s*=\\s*\\[([\\s\\S]*?)\\];`, 's');
        const match = findRegex.exec(content);
        if (!match) return { content, replaced: false };
        const replaceRegex = new RegExp(`(export const ${escapeRegExp(arrayName)}\\s*=\\s*)\\[[\\s\\S]*?\\](;)`, 's');
        const updated = content.replace(replaceRegex, `$1[${newItems.join(', ')}]$2`);
        return { content: updated, replaced: true };
    };

    const replaceObjectArray = (content: string, parent: string, arrayName: string, newItems: string[]): { content: string; replaced: boolean } => {
        const findRegex = new RegExp(`export const ${escapeRegExp(parent)}\\s*=\\s*{[\\s\\S]*?\\b${escapeRegExp(arrayName)}\\s*:\\s*\\[([\\s\\S]*?)\\](?=,|\\s*})`, 's');
        const match = findRegex.exec(content);
        if (!match) return { content, replaced: false };
        const replaceRegex = new RegExp(`(export const ${escapeRegExp(parent)}\\s*=\\s*{[\\s\\S]*?)(\\b${escapeRegExp(arrayName)}\\s*:\\s*)\\[[\\s\\S]*?\\](?=,|\\s*})`, 's');
        const updated = content.replace(replaceRegex, `$1$2[${newItems.join(', ')}]`);
        return { content: updated, replaced: true };
    };

    const getOriginalArrayTokens = (content: string, arrayName: string): string[] | null => {
        // Try direct first
        const directRegex = new RegExp(`export const ${escapeRegExp(arrayName)}\\s*=\\s*\\[([\\s\\S]*?)\\];`, 's');
        const dm = directRegex.exec(content);
        if (dm) return tokenizeArrayItems(dm[1]);
        // Try inside VISUAL_SETTINGS / VISUAL_CONSTANTS
        for (const parent of ['VISUAL_SETTINGS', 'VISUAL_CONSTANTS']) {
            const objRegex = new RegExp(`export const ${escapeRegExp(parent)}\\s*=\\s*{[\\s\\S]*?\\b${escapeRegExp(arrayName)}\\s*:\\s*\\[([\\s\\S]*?)\\](?=,|\\s*})`, 's');
            const om = objRegex.exec(content);
            if (om) return tokenizeArrayItems(om[1]);
        }
        return null;
    };

    groupedByKey.forEach((items, groupKey) => {
        const sorted = [...items].sort((a, b) => (a.groupIndex! - b.groupIndex!));
        if (sorted.length === 0) return;
        const isColorArray = sorted.every(i => i.type === 'color');
        const isNumberArray = sorted.every(i => i.type === 'number');
        const isBooleanArray = sorted.every(i => i.type === 'boolean');
        if (!isColorArray && !isNumberArray && !isBooleanArray) return; // only handle homogeneous arrays

        const originalTokens = getOriginalArrayTokens(updatedContent, groupKey) || [];
        const rendered = sorted.map((item, idx) => {
            if (isNumberArray) return `${item.value}`;
            if (isBooleanArray) return `${item.value.toLowerCase()}`;
            const targetHex = item.value;
            const originalToken = originalTokens[idx] ?? `'${targetHex}'`;
            return convertHexToOriginalFormat(targetHex, originalToken.trim());
        });

        // Try replacing direct array
        let result = replaceDirectArray(updatedContent, groupKey, rendered);
        if (!result.replaced) {
            // Try VISUAL_SETTINGS then VISUAL_CONSTANTS
            for (const parent of ['VISUAL_SETTINGS', 'VISUAL_CONSTANTS']) {
                result = replaceObjectArray(updatedContent, parent, groupKey, rendered);
                if (result.replaced) break;
            }
        }
        updatedContent = result.content;
    });

    // Update the file content in the code array
    updatedCode[constantsFileIndex] = {
        ...updatedCode[constantsFileIndex],
        content: updatedContent
    };
    
    // console.log('Finished applyVisualConstants. Updated code:', updatedCode);
    return updatedCode;
};

const convertColorToHex = (color: string): string => {
    // Handle 0x000000 format
    if (color.startsWith('0x')) {
        // console.log('found 0x color: ', color);
        const hex = '#' + color.slice(2);
        // console.log('converted 0x color to hex: ', hex);
        return hex;
    }
    // Handle rgba(r, g, b, a) format
    const rgbaMatch = color.match(/^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*[\d.]+\s*\)$/i);
    if (rgbaMatch) {
        const r = parseInt(rgbaMatch[1], 10);
        const g = parseInt(rgbaMatch[2], 10);
        const b = parseInt(rgbaMatch[3], 10);
        const hex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        return hex;
    }
    const rgbMatch = color.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (rgbMatch) {
        const r = parseInt(rgbMatch[1], 10);
        const g = parseInt(rgbMatch[2], 10);
        const b = parseInt(rgbMatch[3], 10);
        const hex = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
        return hex;
    }
    // Assume it's already hex or a named color, return as is (for now, focusing on conversion to hex)
    // console.log('Color not 0x or rgba, returning as is: ', color);
    return color;
};

// Helpers
const stripQuotes = (value: string): string => {
    if (
        (value.startsWith("'") && value.endsWith("'")) ||
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith('`') && value.endsWith('`'))
    ) {
        return value.slice(1, -1);
    }
    return value;
};

const stripInlineCommentsOutsideQuotes = (value: string): string => {
    let result = '';
    let inSingle = false;
    let inDouble = false;
    let inBacktick = false;
    let inBlock = false;
    for (let i = 0; i < value.length; i++) {
        const ch = value[i];
        const next = i + 1 < value.length ? value[i + 1] : '';
        if (inBlock) {
            if (ch === '*' && next === '/') {
                inBlock = false;
                i++; // skip '/'
                continue;
            }
            continue; // skip block comment content
        }
        if (!inSingle && !inDouble && !inBacktick) {
            if (ch === '/' && next === '*') {
                inBlock = true;
                i++; // skip '*'
                continue;
            }
            if (ch === '/' && next === '/') {
                // line comment: drop rest
                break;
            }
        }
        if (ch === '\\' && (inSingle || inDouble || inBacktick)) {
            // keep escape and the next char inside strings
            result += ch;
            if (i + 1 < value.length) {
                result += value[i + 1];
                i++;
            }
            continue;
        }
        if (ch === "'" && !inDouble && !inBacktick) {
            inSingle = !inSingle;
        } else if (ch === '"' && !inSingle && !inBacktick) {
            inDouble = !inDouble;
        } else if (ch === '`' && !inSingle && !inDouble) {
            inBacktick = !inBacktick;
        }
        result += ch;
    }
    return result.trimEnd();
};

const isNumericLiteral = (value: string): boolean => {
    return /^-?\d+(?:\.\d+)?(?:[eE][-+]?\d+)?$/.test(value.trim());
};

const isBooleanLiteral = (value: string): boolean => {
    return /^(true|false)$/i.test(value.trim());
};

const isColorToken = (value: string): boolean => {
    if (!value) return false;
    const v = value.trim();
    if (/^0x[0-9a-fA-F]{6}$/.test(v)) return true;
    if (/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v)) return true;
    if (/^rgba\([^)]*\)$/i.test(v)) return true;
    if (/^rgb\([^)]*\)$/i.test(v)) return true;
    return false;
};

const convertHexToOriginalFormat = (hexColor: string, originalFormatValue: string): string => {
    // hexColor will be like '#RRGGBB' (from `constant.value` after user modification)
    // originalFormatValue will be the exact string from the file, e.g., '0xRRGGBB', ''#RRGGBB'', or 'rgba(r,g,b,a)'

    // Ensure 6 characters in hexColor (excluding '#')
    if (hexColor.length !== 7 || !hexColor.startsWith('#')) {
        // If the hex color is not in the expected #RRGGBB format, return the original value
        return originalFormatValue;
    }

    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    if (/^0x/i.test(originalFormatValue)) {
        return `0x${hexColor.slice(1).toLowerCase()}`; // Convert #RRGGBB to 0xrrggbb
    } else if (/^[\'"`]rgba/i.test(originalFormatValue) || /^rgba/i.test(originalFormatValue)) {
        // Extract original alpha if possible, otherwise assume 1.
        const alphaMatch = originalFormatValue.match(/rgba\(\d+,\s*\d+,\s*\d+,\s*([\d.]+)\)/i);
        const alpha = alphaMatch ? parseFloat(alphaMatch[1]) : 1;
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    } else if (/^[\'"`]rgb/i.test(originalFormatValue) || /^rgb/i.test(originalFormatValue)) {
        return `rgb(${r}, ${g}, ${b})`;
    } else if (/^[\'"`]#/.test(originalFormatValue)) {
        const q = originalFormatValue[0];
        return `${q}${hexColor.toLowerCase()}${q}`; // Keep as quoted hex string, preserving quote char
    } else if (originalFormatValue.startsWith("'") || originalFormatValue.startsWith('"') || originalFormatValue.startsWith('`')) {
        // For other quoted string values (e.g., fonts), preserve original quote char
        const q = originalFormatValue[0];
        return `${q}${hexColor}${q}`;
    }
    // Fallback for unexpected formats, return as is (could be a number, boolean, etc. not a color)
    return hexColor;
};

// Split array body into top-level comma-separated tokens, respecting quotes
const tokenizeArrayItems = (body: string): string[] => {
    const items: string[] = [];
    let current = '';
    let inSingle = false;
    let inDouble = false;
    let inBacktick = false;
    let escape = false;
    for (let i = 0; i < body.length; i++) {
        const ch = body[i];
        if (escape) {
            current += ch;
            escape = false;
            continue;
        }
        if (ch === '\\') {
            current += ch;
            escape = true;
            continue;
        }
        if (ch === "'" && !inDouble && !inBacktick) {
            inSingle = !inSingle;
            current += ch;
            continue;
        }
        if (ch === '"' && !inSingle && !inBacktick) {
            inDouble = !inDouble;
            current += ch;
            continue;
        }
        if (ch === '`' && !inSingle && !inDouble) {
            inBacktick = !inBacktick;
            current += ch;
            continue;
        }
        if (ch === ',' && !inSingle && !inDouble && !inBacktick) {
            const trimmed = current.trim();
            if (trimmed.length > 0) items.push(trimmed);
            current = '';
            continue;
        }
        current += ch;
    }
    const last = current.trim();
    if (last.length > 0) items.push(last);
    return items;
};

const escapeRegExp = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');